import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Bell, BellOff, Code, Edit3, Mail, Plus, Settings, Share2 } from 'react-feather';
import { ConfigToolbar, ConfigEmptyState, ToolbarButton } from '../../components/ConfigPages';
import { RunStatePill, EtlLoading, EtlBanner, FilterSelect } from '../../components/EtlPages';
import SchemaJsonEditor from '../../components/SchemaBuilder/SchemaJsonEditor';
import { getEtlConfigCatalog, getSchemaConfig } from '../../utils/api';
import { MobileFilterBar, ChipSelect } from '../../components/FileTracking/EtlMobileKit';
import { useIsMobile } from '../../hooks/use-mobile';
import FeedsCardList from './FeedsCardList';
import FeedActionSheet from './FeedActionSheet';

const EXTRACTION_LABEL = {
  textract: 'Textract OCR',
  formRecognizer: 'Form Recognizer OCR',
  aiExtraction: 'AI extraction',
  'excel-parser': 'Excel parser',
  'csv-parser': 'CSV parser',
  'text-parser': 'Text parser',
  schemaDriven: 'Schema-driven',
};

const extractionLabel = (value) => EXTRACTION_LABEL[value] || value;

const timeAgo = (value) => {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 60) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
};

const outcomeBucket = (lastRun) => {
  if (!lastRun?.status) return 'never';
  if (['normalized', 'resolved'].includes(lastRun.status)) return 'success';
  if (['failed', 'dlq'].includes(lastRun.status)) return 'failure';
  return 'inflight';
};

/** Match keywords, with excludes shown struck through rather than hidden. */
function Keywords({ keywords, excludeKeywords }) {
  const has = (keywords || []).length || (excludeKeywords || []).length;
  if (!has) return <span className="cp-code">—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 260, padding: '6px 0' }}>
      {(keywords || []).map((k) => <span key={`k-${k}`} className="cp-tag cp-mono">{k}</span>)}
      {(excludeKeywords || []).map((k) => (
        <span key={`x-${k}`} className="cp-tag cp-mono" title="Exclude keyword — rejects matches containing this"
              style={{ background: 'var(--color-error-bg)', color: 'var(--color-error-ink)', borderColor: 'transparent', textDecoration: 'line-through' }}>
          {k}
        </span>
      ))}
    </div>
  );
}

/**
 * Feeds — the inventory of every configured integration.
 *
 * This was the "Configurations" view of File Tracking, and it was read-only:
 * it could tell you a feed existed but not take you to the thing that edits
 * it. The Schema Builder, meanwhile, opened on a location picker with no idea
 * which feeds already existed. They are the same object seen from two ends, so
 * the catalog now owns the edit entry point and the wizard opens pre-scoped.
 */
export default function FeedsTab({ scope, refreshTrigger, schemaBuilderEnabled, onCountChange, onConfigureTracking, registerRefresh }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { hotelIdsKey, locationNameById, scopeReady } = scope;
  // Guards a superseded scope's response from overwriting the current one.
  const loadRequestId = useRef(0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState(null);
  const [methodFilter, setMethodFilter] = useState(null);
  const [outcomeFilter, setOutcomeFilter] = useState(null);
  // Raw JSON editor for one feed's Cosmos document.
  const [rawEditor, setRawEditor] = useState(null); // { row, config }
  const [rawLoadingKey, setRawLoadingKey] = useState(null);
  // Phones: the row whose actions sheet is open (kept while it animates out).
  const [sheetRow, setSheetRow] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const locationName = useCallback(
    (hotelId) => locationNameById.get(hotelId) || `Hotel ${hotelId}`,
    [locationNameById],
  );

  const load = useCallback(async () => {
    // Wait for the organization scope to settle — firing on the interim scope
    // means two requests, and the narrower one can land last.
    if (!scopeReady) return;
    const requestId = ++loadRequestId.current;
    const hotelIds = hotelIdsKey ? hotelIdsKey.split(',') : [];
    if (!hotelIds.length) { setRows([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await getEtlConfigCatalog(hotelIds);
      if (loadRequestId.current !== requestId) return;
      setRows(data?.rows || []);
    } catch (err) {
      if (loadRequestId.current !== requestId) return;
      console.error('Failed to load ETL configurations:', err);
      setError('Failed to load feed configurations.');
    } finally {
      if (loadRequestId.current === requestId) setLoading(false);
    }
  }, [hotelIdsKey, scopeReady]);

  useEffect(() => { load(); }, [load, refreshTrigger]);
  useEffect(() => { registerRefresh && registerRefresh(load); }, [registerRefresh, load]);
  useEffect(() => { onCountChange && onCountChange(rows.length); }, [rows.length, onCountChange]);

  const filterOptions = useMemo(() => ({
    locations: [...new Set(rows.map((r) => r.hotelId))]
      .map((id) => ({ value: String(id), label: locationName(id) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    methods: [...new Set(rows.map((r) => r.method))].filter(Boolean)
      .map((m) => ({ value: m, label: m === 'sftp' ? 'SFTP' : m.toUpperCase() })),
    outcomes: [
      { value: 'success', label: 'Processed' },
      { value: 'failure', label: 'Failed / dead letter' },
      { value: 'inflight', label: 'In progress' },
      { value: 'never', label: 'Never run' },
    ],
  }), [rows, locationName]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (locationFilter && String(r.hotelId) !== locationFilter) return false;
      if (methodFilter && r.method !== methodFilter) return false;
      if (outcomeFilter && outcomeBucket(r.lastRun) !== outcomeFilter) return false;
      if (q) {
        const hay = [
          locationName(r.hotelId), r.hotelCode, r.fileType, r.etlFunction, r.ocrService, r.method,
          ...(r.keywords || []), ...(r.excludeKeywords || []), ...(r.allowedExtensions || []),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, searchQuery, locationFilter, methodFilter, outcomeFilter, locationName]);

  /**
   * Open the feed's Cosmos document directly.
   *
   * The wizard is the guided path, but a feed that the wizard cannot express
   * — a hand-tuned dateConfig, a normalizer flag added server-side — could
   * only be reached by picking the location in the schema builder and hoping
   * its probe landed on the right file type. This opens the exact document for
   * the row you clicked.
   */
  const openRawJson = async (row) => {
    const key = `${row.hotelId}-${row.fileType}`;
    setRawLoadingKey(key);
    try {
      const res = await getSchemaConfig(row.hotelId, row.fileType);
      if (res?.success && res.data) {
        setRawEditor({ row, config: res.data });
      } else {
        toast.error('No saved schema document for this feed yet.');
      }
    } catch (err) {
      console.error('Failed to load schema config:', err);
      toast.error(`Could not load the raw config: ${err.message || 'unknown error'}`);
    } finally {
      setRawLoadingKey(null);
    }
  };

  const openWizard = (row) => {
    const qs = row?.fileType ? `?fileType=${encodeURIComponent(row.fileType)}` : '';
    navigate(`/etl-config/schema/${row.hotelId}${qs}`);
  };

  const configureTracking = (r) => onConfigureTracking({
    hotelId: r.hotelId,
    hotelCode: r.hotelCode,
    fileType: r.fileType,
    displayName: r.fileType,
    metadata: { locationName: locationName(r.hotelId) },
  });

  // Radix makes the page inert while the sheet is open, so close it before
  // handing off to a page modal (feed options, raw JSON editor).
  const fromSheet = (fn) => () => { const row = sheetRow; setSheetOpen(false); if (row) fn(row); };

  if (error) return <EtlBanner tone="error" title="Could not load">{error}</EtlBanner>;

  const newFeedButton = schemaBuilderEnabled ? (
    <ToolbarButton variant="primary" icon={Plus} onClick={() => navigate('/etl-config/schema')}>
      New feed
    </ToolbarButton>
  ) : null;
  const countMeta = filtered.length === rows.length ? `${rows.length} feeds` : `${filtered.length} of ${rows.length}`;

  const emptyOrList = (list) => (loading ? (
    <EtlLoading>Loading feed configurations…</EtlLoading>
  ) : !rows.length ? (
    <ConfigEmptyState
      title="No feeds configured"
      body="A feed tells the pipeline what file to expect, how to recognise it, and how to read it. Create one to start ingesting data for these locations."
      action={schemaBuilderEnabled ? (
        <ToolbarButton variant="primary" icon={Plus} onClick={() => navigate('/etl-config/schema')}>Create the first feed</ToolbarButton>
      ) : null}
    />
  ) : !filtered.length ? (
    <ConfigEmptyState title="No feeds match these filters" body="Clear the search or filters to see the full inventory." />
  ) : list);

  const rawEditorModal = rawEditor ? (
    <SchemaJsonEditor
      initialConfig={rawEditor.config}
      hotelId={rawEditor.row.hotelId}
      fileType={rawEditor.row.fileType}
      onClose={() => setRawEditor(null)}
      onSave={() => { setRawEditor(null); load(); }}
    />
  ) : null;

  // Phones: full-width search over a scrolling row of filter chips, feeds as
  // cards grouped by property, and the row actions in a bottom sheet.
  if (isMobile) {
    return (
      <div>
        <MobileFilterBar
          search={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search feeds, keywords, functions…' }}
          bar={<><span className="cp-meta">{countMeta}</span>{newFeedButton}</>}
        >
          <ChipSelect value={locationFilter} options={filterOptions.locations} onChange={setLocationFilter} placeholder="All locations" ariaLabel="Filter by location" />
          <ChipSelect value={methodFilter} options={filterOptions.methods} onChange={setMethodFilter} placeholder="All methods" ariaLabel="Filter by method" />
          <ChipSelect value={outcomeFilter} options={filterOptions.outcomes} onChange={setOutcomeFilter} placeholder="All outcomes" ariaLabel="Filter by outcome" />
        </MobileFilterBar>

        {emptyOrList(
          <FeedsCardList
            rows={filtered}
            locationName={locationName}
            extractionLabel={extractionLabel}
            onOpen={(r) => { setSheetRow(r); setSheetOpen(true); }}
          />,
        )}

        <FeedActionSheet
          row={sheetRow}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          locationName={sheetRow ? locationName(sheetRow.hotelId) : null}
          extractionLabel={extractionLabel}
          schemaBuilderEnabled={schemaBuilderEnabled}
          rawLoading={!!sheetRow && rawLoadingKey === `${sheetRow.hotelId}-${sheetRow.fileType}`}
          onConfigure={fromSheet(configureTracking)}
          onRawJson={fromSheet(openRawJson)}
          onEdit={fromSheet(openWizard)}
        />

        {rawEditorModal}
      </div>
    );
  }

  return (
    <div className="it-frame">
      <ConfigToolbar
        search={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search feeds, keywords, functions…' }}
        meta={countMeta}
        actions={newFeedButton}
      >
        <FilterSelect value={locationFilter} options={filterOptions.locations} onChange={setLocationFilter} placeholder="All locations" ariaLabel="Filter by location" />
        <FilterSelect width={140} value={methodFilter} options={filterOptions.methods} onChange={setMethodFilter} placeholder="All methods" ariaLabel="Filter by method" />
        <FilterSelect width={160} value={outcomeFilter} options={filterOptions.outcomes} onChange={setOutcomeFilter} placeholder="All outcomes" ariaLabel="Filter by outcome" />
      </ConfigToolbar>

      {emptyOrList(
        <div className="it-scroll">
          <table className="it-tbl">
            <thead>
              <tr>
                <th>Property</th>
                <th>Feed</th>
                <th>Matches on</th>
                <th>Formats</th>
                <th>Arrives by</th>
                <th>Extraction</th>
                <th>Alerts</th>
                <th>Last received</th>
                <th>Last outcome</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const extraction = r.lastRun?.ocrService || r.ocrService;
                const errorTitle = r.lastRun?.lastErrorMessage
                  ? `${r.lastRun.lastErrorStage ? `[${r.lastRun.lastErrorStage}] ` : ''}${r.lastRun.lastErrorMessage}`
                  : undefined;
                return (
                  <tr key={`${r.hotelId}-${r.fileType}`} className="cp-row">
                    <td className="cp-cell">
                      <div className="cp-name">{locationName(r.hotelId)}</div>
                      {r.hotelCode ? <div className="cp-code">{r.hotelCode}</div> : null}
                    </td>
                    <td className="cp-cell">
                      <div className="cp-name" style={{ textTransform: 'capitalize' }}>{r.fileType}</div>
                      {r.etlFunction ? <div className="cp-code">{r.etlFunction}</div> : null}
                    </td>
                    <td className="cp-cell">
                      <Keywords keywords={r.keywords} excludeKeywords={r.excludeKeywords} />
                      <div className="cp-code">{r.matchType === 'subject' ? 'in email subject' : 'in file name'}</div>
                    </td>
                    <td className="cp-cell">
                      {r.allowedExtensions?.length
                        ? <span className="cp-mono">{r.allowedExtensions.join(', ')}</span>
                        : <span className="cp-code">Any</span>}
                    </td>
                    <td className="cp-cell">
                      <span className="cp-tag">
                        {r.method === 'sftp' ? <Share2 size={12} /> : <Mail size={12} />}
                        <span style={{ marginLeft: 5 }}>{r.method === 'sftp' ? 'SFTP' : 'Email'}</span>
                      </span>
                    </td>
                    <td className="cp-cell">
                      <span style={{ fontSize: 12.5, color: 'var(--lodge-onyx-600)' }}>
                        {extraction ? extractionLabel(extraction) : '—'}
                      </span>
                    </td>
                    <td className="cp-cell">
                      {r.trackingEnabled
                        ? <span className="cp-tag cp-ok"><Bell size={11} /><span style={{ marginLeft: 5 }}>On</span></span>
                        : <span className="cp-tag"><BellOff size={11} /><span style={{ marginLeft: 5 }}>Off</span></span>}
                    </td>
                    <td className="cp-cell">
                      {r.lastRun?.receivedAt ? (
                        <div title={new Date(r.lastRun.receivedAt).toLocaleString()}>
                          <div className="cp-name">{timeAgo(r.lastRun.receivedAt)}</div>
                          <div className="cp-code">{new Date(r.lastRun.receivedAt).toLocaleDateString()}</div>
                        </div>
                      ) : <span className="cp-code">—</span>}
                    </td>
                    <td className="cp-cell">
                      <RunStatePill status={r.lastRun?.status || 'never'} title={errorTitle} />
                    </td>
                    <td className="cp-cell-tight" style={{ textAlign: 'right' }}>
                      <span className="cp-row-actions">
                        <ToolbarButton
                          small iconOnly variant="ghost" icon={Settings}
                          title="Alert schedule and recipients"
                          aria-label={`Tracking options for ${r.fileType}`}
                          onClick={() => configureTracking(r)}
                        />
                        <ToolbarButton
                          small iconOnly variant="ghost" icon={Code}
                          title="Open this feed's raw JSON config"
                          aria-label={`Raw JSON for ${r.fileType}`}
                          disabled={rawLoadingKey === `${r.hotelId}-${r.fileType}`}
                          onClick={() => openRawJson(r)}
                        />
                        {schemaBuilderEnabled ? (
                          <ToolbarButton
                            small iconOnly variant="ghost" icon={Edit3}
                            title="Edit this feed's schema in the wizard"
                            aria-label={`Edit schema for ${r.fileType}`}
                            onClick={() => openWizard(r)}
                          />
                        ) : null}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>,
      )}

      {rawEditorModal}
    </div>
  );
}
